import { useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/config/constants';
import { createPodReceiptPath } from '@/api/pod';
import { getActiveSessionRevision, getAuthTokenForRequest, subscribeActiveSessionRevision } from '@/api/authenticatedClient';

const identity = binding => JSON.stringify([binding?.user?.id, binding?.user?.tenant_id,
  binding?.user?.roles || [binding?.user?.role], binding?.source, binding?.revision]);
const destination = () => JSON.stringify([API, window.location.origin,
  window.lvbDesktop?.backendUrl, window.lvbDesktop?.localBackendUrl]);

// TCA-26102006: custody belongs to this opening, through clipboard settlement.
// Retirement prevents new side effects; it cannot undo an issued link or copy.
export default function useReceiptLinkCopy(orderId, enabled) {
  const { sessionBinding: binding } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const principal = identity(binding);
  const target = destination();
  const scope = JSON.stringify([orderId, enabled, principal, revision, target]);
  // A–B–A is a new opening even though its semantic key matches the first A.
  const opening = useMemo(() => ({ scope }), [scope]);
  const latest = useRef(null);
  latest.current = { opening, binding };
  const mounted = useRef(false);
  const attempt = useRef(null);
  const [view, setView] = useState(null);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      const previous = attempt.current;
      if (previous) {
        previous.retired = true;
        clearTimeout(previous.timer);
        previous.controller.abort();
        attempt.current = null;
      }
    };
  }, [opening]);

  const copy = async () => {
    if (!enabled || !orderId || !mounted.current || attempt.current
        || latest.current.opening !== opening || !binding?.user?.id
        || !['cloud', 'offline'].includes(binding.source)
        || identity(binding) !== principal || destination() !== target
        || binding.revision !== getActiveSessionRevision()) return;
    const requestUrl = `${API}/pod/${encodeURIComponent(orderId)}/link`;
    const token = getAuthTokenForRequest(requestUrl);
    if (!token) { toast.error('Sign in to copy a receipt link.'); return; }
    const operation = { controller: new AbortController(), retired: false };
    const origin = window.location.origin;
    const current = () => mounted.current && !operation.retired && attempt.current === operation
      && latest.current.opening === opening && identity(latest.current.binding) === principal
      && getActiveSessionRevision() === revision && destination() === target
      && getAuthTokenForRequest(requestUrl) === token;
    attempt.current = operation;
    setView({ opening, pending: true, waiting: false });
    operation.timer = setTimeout(() => {
      if (current()) setView({ opening, pending: true, waiting: true });
    }, 30000);
    let linkConfirmed = false;
    try {
      if (!current()) return;
      const path = await createPodReceiptPath(orderId, { token, signal: operation.controller.signal });
      if (!current()) return;
      linkConfirmed = true;
      const url = `${origin}${path}`;
      let copied = false;
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url);
          copied = true;
        } catch { /* Only the still-current opening may try the fallback. */ }
      }
      if (!current()) return;
      if (!copied) {
        const textarea = document.createElement('textarea');
        try {
          textarea.value = url;
          textarea.style.position = 'fixed';
          textarea.style.left = '-9999px';
          document.body.appendChild(textarea);
          textarea.select();
          // Focus/selection can synchronously retire the opening.
          if (!current()) return;
          copied = document.execCommand('copy') === true;
        } finally { textarea.remove(); }
      }
      if (current()) {
        if (copied) toast.success('Receipt link copied');
        else toast.error('Receipt link created, but copying failed.');
      }
    } catch {
      if (current()) toast.error(linkConfirmed ? 'Receipt link created, but copying failed.'
        : 'Receipt link creation could not be confirmed. No link was copied.');
    } finally {
      clearTimeout(operation.timer);
      if (current()) setView({ opening, pending: false, waiting: false });
      if (attempt.current === operation) attempt.current = null;
    }
  };
  return { copy, pending: view?.opening === opening && view.pending,
    waiting: view?.opening === opening && view.waiting };
}
